/**
 * Shop the Look Grid
 *
 * Handles:
 * - Card deck stacking with data-position attributes
 * - Arrow navigation within lightbox/drawer
 * - Hotspot → open lightbox (desktop) / drawer (mobile)
 * - Close via X button or overlay tap
 * - Deck swipe (outside active card)
 * - Ghost image swap (2nd product image as default)
 * - Reset to ghost on close
 * - Sibling swatch ghost swap
 */

(function () {
  const SELECTORS = {
    item: '.stl-grid__item',
    deck: '[data-deck]',
    card: '.shoppable-image-editorial__card',
    hotspot: '.shoppable-image-editorial__hotspot',
    prevArrow: '.shoppable-image-editorial__arrow--prev',
    nextArrow: '.shoppable-image-editorial__arrow--next',
    currentPage: '[data-current]',
    carouselArea: '.shoppable-image-editorial__carousel-area',
    overlay: '[data-overlay]',
    drawerClose: '[data-drawer-close]',
  };

  class ShoppableLookGrid {
    constructor(item) {
      this.item = item;
      this.deck = item.querySelector(SELECTORS.deck);
      this.cards = [...item.querySelectorAll(SELECTORS.card)];
      this.hotspots = [...item.querySelectorAll(SELECTORS.hotspot)];
      this.prevArrow = item.querySelector(SELECTORS.prevArrow);
      this.nextArrow = item.querySelector(SELECTORS.nextArrow);
      this.currentPageEl = item.querySelector(SELECTORS.currentPage);
      this.carouselArea = item.querySelector(SELECTORS.carouselArea);
      this.overlay = item.querySelector(SELECTORS.overlay);
      this.drawerClose = item.querySelector(SELECTORS.drawerClose);

      this.currentIndex = 0;

      if (!this.deck || this.cards.length === 0) return;

      this.init();
    }

    init() {
      this.bindArrows();
      this.bindHotspots();
      this.bindClose();
      this.bindDeckSwipe();
      this.preloadSwatchGhosts();
      this.updateDeck();
    }

    // -----------------------------------------------------------------------
    // Card deck positioning
    // -----------------------------------------------------------------------

    updateDeck() {
      const total = this.cards.length;

      this.cards.forEach((card, i) => {
        const diff = i - this.currentIndex;

        if (diff === 0) {
          card.dataset.position = 'active';
        } else if (diff === 1 || (this.currentIndex === total - 1 && i === 0 && total > 2)) {
          card.dataset.position = 'next';
        } else if (diff === -1 || (this.currentIndex === 0 && i === total - 1 && total > 2)) {
          card.dataset.position = 'prev';
        } else {
          card.dataset.position = 'hidden';
        }
      });

      // Update hotspots
      this.hotspots.forEach((hotspot, i) => {
        hotspot.classList.toggle('is-active', i === this.currentIndex);
      });

      // Update pagination
      if (this.currentPageEl) {
        this.currentPageEl.textContent = this.currentIndex + 1;
      }

      // Arrows always enabled (looping)
      if (this.prevArrow) this.prevArrow.disabled = false;
      if (this.nextArrow) this.nextArrow.disabled = false;
    }

    goTo(index) {
      if (index < 0 || index >= this.cards.length) return;
      this.currentIndex = index;
      this.updateDeck();
    }

    goNext() {
      this.goTo((this.currentIndex + 1) % this.cards.length);
    }

    goPrev() {
      this.goTo((this.currentIndex - 1 + this.cards.length) % this.cards.length);
    }

    // -----------------------------------------------------------------------
    // Arrows
    // -----------------------------------------------------------------------

    bindArrows() {
      if (this.prevArrow) {
        this.prevArrow.addEventListener('click', () => this.goPrev());
      }
      if (this.nextArrow) {
        this.nextArrow.addEventListener('click', () => this.goNext());
      }
    }

    // -----------------------------------------------------------------------
    // Hotspots — always open lightbox/drawer on ALL breakpoints
    // -----------------------------------------------------------------------

    bindHotspots() {
      this.hotspots.forEach((hotspot) => {
        hotspot.addEventListener('click', () => {
          const index = parseInt(hotspot.dataset.hotspotIndex, 10);
          if (isNaN(index)) return;

          this.goTo(index);
          this.open();
        });
      });
    }

    // -----------------------------------------------------------------------
    // Open / Close (CSS handles lightbox vs drawer based on breakpoint)
    // -----------------------------------------------------------------------

    bindClose() {
      if (this.drawerClose) {
        this.drawerClose.addEventListener('click', () => this.close());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', () => this.close());
      }

      // ESC key closes
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.item.classList.contains('is-drawer-open')) {
          this.close();
        }
      });
    }

    open() {
      this.item.classList.add('is-drawer-open');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.resetToGhost();
      this.item.classList.remove('is-drawer-open');
      setTimeout(() => {
        if (!document.querySelector('.stl-grid__item.is-drawer-open')) {
          document.body.style.overflow = '';
        }
      }, 400);
    }

    // -----------------------------------------------------------------------
    // Deck swipe — only fires when touch starts OUTSIDE the active card
    // -----------------------------------------------------------------------

    bindDeckSwipe() {
      if (!this.deck) return;

      let startX = 0;
      let startY = 0;
      let isSwiping = false;
      let touchedActiveCard = false;

      this.deck.addEventListener('touchstart', (e) => {
        const activeCard = this.deck.querySelector('[data-position="active"]');
        touchedActiveCard = activeCard && activeCard.contains(e.target);

        if (touchedActiveCard) return;

        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        isSwiping = false;
      }, { passive: true });

      this.deck.addEventListener('touchmove', (e) => {
        if (touchedActiveCard || !startX) return;

        const dx = Math.abs(e.touches[0].clientX - startX);
        const dy = Math.abs(e.touches[0].clientY - startY);

        if (dx > dy && dx > 10) {
          isSwiping = true;
        }
      }, { passive: true });

      this.deck.addEventListener('touchend', (e) => {
        if (touchedActiveCard || !isSwiping) {
          startX = 0;
          startY = 0;
          isSwiping = false;
          touchedActiveCard = false;
          return;
        }

        const dx = e.changedTouches[0].clientX - startX;

        if (Math.abs(dx) > 40) {
          if (dx < 0) {
            this.goNext();
          } else {
            this.goPrev();
          }
        }

        startX = 0;
        startY = 0;
        isSwiping = false;
        touchedActiveCard = false;
      }, { passive: true });
    }


    // -----------------------------------------------------------------------
    // Preload ghost images for all sibling swatches
    // -----------------------------------------------------------------------

    preloadSwatchGhosts() {
      this.cards.forEach((card) => {
        if (!card.dataset.ghostSrc) return;

        const swatches = card.querySelectorAll('.collection-card-swatch');
        swatches.forEach((swatch) => {
          try {
            const images = JSON.parse(swatch.dataset.imagesJson);
            if (images && images.length > 1) {
              const img = new Image();
              img.src = images[1];
            }
          } catch (e) {}
        });
      });
    }

    // -----------------------------------------------------------------------
    // Reset cards to ghost image when drawer/lightbox closes
    // -----------------------------------------------------------------------

    resetToGhost() {
      // The tile owns image ordering — slide 0 is already the ghost.
      // Just rewind the scroll position on close.
      this.cards.forEach((card) => {
        const track = card.querySelector('.collection-card-carousel-track');
        if (!track) return;
        track.scrollLeft = 0;
        if (track.parentElement) track.parentElement.scrollLeft = 0;
      });
    }
  }


  // =========================================================================
  // Initialise
  // =========================================================================

  function initAllItems() {
    document.querySelectorAll(SELECTORS.item).forEach((item) => {
      if (item.dataset.initialised) return;
      item.dataset.initialised = 'true';
      new ShoppableLookGrid(item);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllItems);
  } else {
    initAllItems();
  }

  // Shopify theme editor
  document.addEventListener('shopify:section:load', (e) => {
    const items = e.target.querySelectorAll(SELECTORS.item);
    items.forEach((item) => {
      item.removeAttribute('data-initialised');
      new ShoppableLookGrid(item);
    });
  });

  // MutationObserver for dynamic content
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches?.(SELECTORS.item) && !node.dataset.initialised) {
          node.dataset.initialised = 'true';
          new ShoppableLookGrid(node);
        }
        const nested = node.querySelectorAll?.(SELECTORS.item);
        if (nested) {
          nested.forEach((el) => {
            if (!el.dataset.initialised) {
              el.dataset.initialised = 'true';
              new ShoppableLookGrid(el);
            }
          });
        }
      }
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
})();